import axios from "axios";
import { extractError } from "../../utils/apiErrorUtils";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

/**
 * Safe Spots — venues that agreed to host trades.
 *
 * One module for both halves, because the split that matters is not read vs
 * write but WHO may see the consent record. The reads below are open to any
 * signed-in collector and never carry it; the admin calls are the only ones that
 * do, and they are behind `verifyAdmin` on the server.
 *
 * ── There is deliberately no "near me" call ──
 * Not here, and none on the server either. Asking the API for venues near a
 * position would put the user's coordinates in a query string, and from there
 * into access logs, proxy logs and error reports — the exact leak the trade
 * check-in flow goes to such lengths to avoid. The whole active list arrives
 * instead and `safeSpots/safeSpotDistance` orders it on the device, so distance
 * ranking costs nothing in exposure.
 *
 * `bbox` is a VIEWPORT — where the map is looking, which is often not where the
 * user is — and stays optional.
 */

/**
 * What the map shows: active venues only, with no consent record attached.
 *
 * Resolves to the ARRAY of venues. The endpoint answers `{ spots, truncated }`
 * so it can say when it capped the list, but every caller here wants the venues;
 * `getSafeSpotsForMapPage` below is for the one that needs to know about the cap.
 */
export const listSafeSpotsForMap = async () => {
  try {
    const { spots } = await getSafeSpotsForMapPage();
    return spots;
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * The full response, including `truncated`.
 *
 * Told rather than inferred, so a client can say "zoom in to see more" instead
 * of silently rendering a partial map.
 */
export const getSafeSpotsForMapPage = async ({ bbox } = {}) => {
  try {
    const params = bbox ? { bbox: bbox.join(",") } : undefined;
    const { data } = await axios.get(`${apiUrl}/safe-spots/map`, { params });
    return {
      spots: Array.isArray(data?.spots) ? data.spots : [],
      truncated: Boolean(data?.truncated),
    };
  } catch (error) {
    return Promise.reject(extractError(error, "Could not load Safe Spots"));
  }
};

/**
 * One venue.
 *
 * Needed when a thread references a spot the map never loaded — an old
 * conversation, or building a calendar event whose address has to come from the
 * venue record rather than from the message. A paused venue still resolves, and
 * says so, because two people who already agreed to meet there still need it.
 */
export const getSafeSpot = async (spotId) => {
  try {
    const { data } = await axios.get(`${apiUrl}/safe-spots/${spotId}`);
    return data;
  } catch (error) {
    return Promise.reject(extractError(error, "Could not load that Safe Spot"));
  }
};

/** The admin list — the one response that carries who agreed to each venue. */
export const listSafeSpots = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/safe-spots`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to load safe spots"));
  }
};

export const createSafeSpot = async (spot) => {
  try {
    const { data } = await axios.post(`${apiUrl}/safe-spots`, spot);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to create the safe spot"));
  }
};

export const updateSafeSpot = async (spotId, updates) => {
  try {
    const { data } = await axios.put(`${apiUrl}/safe-spots/${spotId}`, updates);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to update the safe spot"));
  }
};

export const deactivateSafeSpot = async (spotId) => {
  try {
    const { data } = await axios.delete(`${apiUrl}/safe-spots/${spotId}`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(extractError(error, "Failed to unlist the safe spot"));
  }
};
